import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from 'components/ui/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import DeleteConfirmModal from 'components/ui/DeleteConfirmModal';
import PipelineMetrics from './components/PipelineMetrics';
import PipelineKanban from './components/PipelineKanban';
import PipelineStagesModal from './components/PipelineStagesModal';
import OpportunitiesFormModal from '../opportunities/components/OpportunitiesFormModal';
import { pipelineApi } from 'api/deals';
import useReps from 'hooks/useReps';
import { getCurrentUser } from 'api/client';
import { useToast } from 'components/ui/Toast';

export default function SalesPipeline() {
  const reps = useReps();
  const currentUser = getCurrentUser();
  const meName = currentUser?.fullName || currentUser?.email || 'Owner';
  const toast = useToast();
  const repOptions = [
    { value: 'all', label: 'All Reps' },
    ...(reps || []).map((r) => ({ value: r, label: r })),
    { value: 'Unassigned', label: 'Unassigned' },
  ];
  const [filterRep, setFilterRep] = useState('all');
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteDealId, setDeleteDealId] = useState(null);
  const [board, setBoard] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [stageConfig, setStageConfig] = useState(null);
  const [showStagesModal, setShowStagesModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const stageFlags = useMemo(() => {
    const cfg = stageConfig?.stages || [];
    const won = cfg.find((s) => s.isWon)?.id ?? 5;
    const lost = cfg.find((s) => s.isClosed && !s.isWon)?.id ?? 6;
    return { wonStage: won, lostStage: lost };
  }, [stageConfig]);

  const loadPipelineData = async () => {
    const configResponse = await pipelineApi.getConfig();
    const cfg = configResponse?.stages || [];
    const wonStage = cfg.find((s) => s.isWon)?.id ?? 5;
    const lostStage = cfg.find((s) => s.isClosed && !s.isWon)?.id ?? 6;
    const [metricsResponse, boardResponse] = await Promise.all([
      pipelineApi.getMetrics({ wonStage, lostStage }),
      pipelineApi.getBoard(),
    ]);

    return {
      metricsResponse,
      boardResponse,
      configResponse,
    };
  };

  const buildOpportunityPayload = (deal) => ({
    name: deal?.name?.trim(),
    accountName: deal?.company?.trim(),
    ownerName: deal?.rep || 'Unassigned',
    stage: Number(deal?.stage ?? 1),
    amount: Number(deal?.value || 0),
    probabilityPercent: Number(deal?.probability || 0),
    expectedCloseDateUtc: new Date(deal?.closeDate || Date.now()).toISOString(),
    description: deal?.source || null,
  });

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await loadPipelineData();

        if (!isMounted) {
          return;
        }

        setMetrics(data.metricsResponse);
        setBoard(data.boardResponse);
        setStageConfig(data.configResponse);
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || 'Unable to load the pipeline.');
          setMetrics(null);
          setBoard(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const metricCards = useMemo(() => {
    if (!metrics) {
      return undefined;
    }

    const toCr = (value) => `₹${(value / 10000000).toFixed(1)}Cr`;
    const toL = (value) => `₹${(value / 100000).toFixed(1)}L`;

    return [
      { label: 'Total Pipeline', value: toCr(metrics.totalPipeline), change: null, icon: 'IndianRupee', color: '#3B82F6', bg: '#DBEAFE' },
      { label: 'Deals in Pipeline', value: String(metrics.dealsInPipeline), change: null, icon: 'Briefcase', color: '#7C3AED', bg: '#EDE9FE' },
      { label: 'Avg Deal Size', value: toL(metrics.averageDealSize), change: null, icon: 'TrendingUp', color: '#D97706', bg: '#FEF3C7' },
      { label: 'Win Rate', value: `${metrics.winRatePercent}%`, change: null, icon: 'Trophy', color: '#15803D', bg: '#DCFCE7' },
      { label: 'Avg Sales Cycle', value: `${metrics.averageSalesCycleDays} days`, change: null, icon: 'Clock', color: '#0891B2', bg: '#CFFAFE' },
      { label: 'Forecasted Rev', value: toCr(metrics.forecastedRevenue), change: null, icon: 'BarChart2', color: '#B91C1C', bg: '#FEE2E2' },
    ];
  }, [metrics]);

  const applyPipelineData = (data) => {
    setMetrics(data.metricsResponse);
    setBoard(data.boardResponse);
    setStageConfig(data.configResponse);
  };

  const handleMoveStage = async (dealId, stage) => {
    try {
      await pipelineApi.moveDealStage(dealId, stage, stageFlags);
      applyPipelineData(await loadPipelineData());
    } catch (error) {
      throw error;
    }
  };

  const handleDealSubmit = async (formData) => {
    const payload = buildOpportunityPayload(formData);
    try {
      if (selectedDeal?.id) {
        await pipelineApi.updateDeal(selectedDeal.id, payload);
        toast.success('Deal updated.');
      } else {
        await pipelineApi.createDeal(payload);
        toast.success('Deal created.');
      }

      applyPipelineData(await loadPipelineData());
      setShowAddDeal(false);
      setSelectedDeal(null);
    } catch (error) {
      toast.error(error?.message || 'Could not save the deal.');
    }
  };

  const handleEditDeal = (deal) => {
    setSelectedDeal(deal);
    setShowAddDeal(true);
  };

  const handleDeleteDealRequest = (dealId) => {
    setDeleteDealId(dealId);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteDealId) {
      return;
    }

    try {
      await pipelineApi.removeDeal(deleteDealId);
      toast.success('Deal deleted.');
      applyPipelineData(await loadPipelineData());
      setIsDeleteOpen(false);
      setDeleteDealId(null);
    } catch (error) {
      toast.error(error?.message || 'Could not delete the deal.');
    }
  };

  const handleStagesChanged = async () => {
    try {
      applyPipelineData(await loadPipelineData());
    } catch (error) {
      toast.error(error?.message || 'Could not refresh the pipeline.');
    }
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-heading font-bold text-2xl md:text-3xl"
              style={{ color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}
            >
              Sales Pipeline
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Visual deal progression — drag cards between stages to update
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Rep filter */}
            <select
              value={filterRep}
              onChange={(e) => setFilterRep(e?.target?.value)}
              className="text-sm px-3 py-2 rounded-lg border font-caption flex-shrink-0"
              style={{
                borderColor: 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-foreground)',
                height: 38,
              }}
            >
              {repOptions?.map((o) => (
                <option key={o?.value} value={o?.value}>{o?.label}</option>
              ))}
            </select>
            {(currentUser?.role === 'Owner' || currentUser?.role === 'Manager') && (
              <Button
                variant="outline"
                size="sm"
                iconName="Settings2"
                iconPosition="left"
                iconSize={14}
                onClick={() => setShowStagesModal(true)}
              >
                Manage Stages
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              iconSize={14}
              onClick={() => {
                setSelectedDeal(null);
                setShowAddDeal(true);
              }}
            >
              Add Deal
            </Button>
          </div>
        </div>

        {/* Metrics */}
        <div className="mb-6">
          <PipelineMetrics metrics={metricCards} />
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
              onClick={async () => {
                setIsLoading(true);
                setLoadError('');
                try {
                  applyPipelineData(await loadPipelineData());
                } catch (error) {
                  setLoadError(error?.message || 'Unable to load the pipeline.');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Deals per stage — real counts from the board */}
        <div
          className="flex items-center gap-4 px-4 py-3 rounded-xl mb-5 overflow-x-auto"
          style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
        >
          <span className="font-caption text-xs font-semibold flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }}>
            Deals by stage:
          </span>
          {(board?.columns || []).filter((c) => (c?.deals || []).length > 0)?.map((c, i) => (
            <div key={c?.stage ?? i} className="flex items-center gap-2 flex-shrink-0">
              <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>{c?.name || `Stage ${c?.stage}`}</span>
              <span className="font-caption text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                {c?.deals?.length ?? 0} {((c?.deals?.length ?? 0) === 1) ? 'deal' : 'deals'}
              </span>
              {i < (board?.columns || []).filter((x) => (x?.deals || []).length > 0).length - 1 && (
                <div className="w-px h-4 mx-1" style={{ background: 'var(--color-border)' }} />
              )}
            </div>
          ))}
          {!board?.columns?.some((c) => (c?.deals || []).length > 0) && (
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              No deals yet — add one to see stage distribution
            </span>
          )}
        </div>

        {/* Kanban Board */}
        {isLoading ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading pipeline...</p>
          </div>
        ) : (
        <PipelineKanban
          filterRep={filterRep}
          board={board}
          stages={stageConfig?.stages}
          onMoveStage={handleMoveStage}
          onEditDeal={handleEditDeal}
          onDeleteDeal={handleDeleteDealRequest}
        />
        )}

        {/* Footer */}
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Drag &amp; drop deals to update pipeline stages automatically
          </p>
        </div>

        <OpportunitiesFormModal
          isOpen={showAddDeal}
          opportunity={selectedDeal}
          stages={stageConfig?.stages}
          onClose={() => {
            setShowAddDeal(false);
            setSelectedDeal(null);
          }}
          onSubmit={handleDealSubmit}
        />

        <PipelineStagesModal
          isOpen={showStagesModal}
          stages={stageConfig?.stages}
          onClose={() => setShowStagesModal(false)}
          onChanged={handleStagesChanged}
        />

        <DeleteConfirmModal
          isOpen={isDeleteOpen}
          itemName="Deal"
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteDealId(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      </div>
    </AppLayout>
  );
}
